import React, { useEffect, useState } from 'react';
import { ArrowLeft, Mail, ShieldAlert } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import Background from '../Login/Background';
import { useFreeEventLimit } from '../../hooks/useFreeEventLimit';
import { getOrganizerProfile } from '../../api';
import { OrganizerProfile } from '../../types/Common';

const FreeEventLimitReached: React.FC = () => {
  const navigate = useNavigate();
  const limitInfo = useFreeEventLimit(true);
  const [organizer, setOrganizer] = useState<OrganizerProfile | null>(null);
  const [isOrganizerLoading, setIsOrganizerLoading] = useState(true);
  const [organizerError, setOrganizerError] = useState('');

  useEffect(() => {
    let isMounted = true;

    const loadOrganizer = async () => {
      try {
        const profile = await getOrganizerProfile();
        if (isMounted) {
          setOrganizer(profile);
          setOrganizerError('');
        }
      } catch (error) {
        console.error('Error loading organizer details:', error);
        if (isMounted) {
          setOrganizerError('Unable to load organizer details for the email draft.');
        }
      } finally {
        if (isMounted) {
          setIsOrganizerLoading(false);
        }
      }
    };

    loadOrganizer();
    return () => {
      isMounted = false;
    };
  }, []);

  const contactAdministrator = () => {
    if (!organizer) return;

    const organizerName =
      organizer.name ||
      `${organizer.firstName || ''} ${organizer.lastName || ''}`.trim() ||
      'Organizer';
    const subject = 'Request to increase free event limit';
    const body = [
      'Hello Eventox team,',
      '',
      'I have reached the free event limit for my organizer account. Please increase my free event limit.',
      '',
      'Organizer details:',
      `Name: ${organizerName}`,
      `Phone: ${organizer.phone || 'Not available'}`,
      `Email: ${organizer.email || 'Not available'}`,
      '',
      'Thank you.',
    ].join('\n');

    window.location.href = `mailto:info@eventox.co?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  };

  return (
    <div className="position-relative min-vh-100 d-flex align-items-center justify-content-center">
      <Background />
      <div className="v3-wrap w-100 px-3 px-md-4 py-4" style={{ maxWidth: 1200 }}>
        <div className="glass-panel mx-auto text-center" style={{ maxWidth: 620 }}>
          <div
            className="d-inline-flex align-items-center justify-content-center rounded-4 mb-3"
            style={{
              width: 64,
              height: 64,
              color: '#EE3F7C',
              background: 'rgba(238, 63, 124, 0.12)',
              border: '1px solid rgba(238, 63, 124, 0.3)',
            }}
          >
            <ShieldAlert size={30} />
          </div>

          <h2
            className="font-jakarta fw-bold mb-2"
            style={{
              background: 'linear-gradient(135deg, #EE3F7C, #F79B59)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
            }}
          >
            Limit reached
          </h2>
          <p className="contact-header-subtitle mx-auto mb-4" style={{ maxWidth: 520 }}>
            You've used every free event included on your account.
            Contact administrator to have your allowance increased.
          </p>

          {limitInfo.isLoading ? (
            <div className="contact-header-subtitle py-4" role="status">
              Loading event limit details...
            </div>
          ) : limitInfo.error ? (
            <div className="alert alert-danger text-start" role="alert">
              {limitInfo.error}
            </div>
          ) : (
            <div
              className="d-grid mb-4"
              style={{
                gridTemplateColumns: 'repeat(3, 1fr)',
                border: '1px solid rgba(255,255,255,0.1)',
                borderRadius: 14,
                overflow: 'hidden',
              }}
            >
              {[
                ['Created', limitInfo.created],
                ['Limit', limitInfo.limit],
                ['Remaining', limitInfo.remaining],
              ].map(([label, value], index) => (
                <div
                  key={label}
                  className="p-3"
                  style={{
                    borderLeft: index === 0 ? 'none' : '1px solid rgba(255,255,255,0.1)',
                  }}
                >
                  <div
                    className="eventox-metric-value fw-bold mb-0 mt-1 lh-1 font-mono"
                    style={label === 'Remaining' ? {
                      background: 'linear-gradient(135deg, #EE3F7C, #F79B59)',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                      backgroundClip: 'text',
                    } : { color: '#fff' }}
                  >
                    {value ?? '-'}
                  </div>
                  <div
                    className="fs-11px font-mono text-uppercase ls-1 fw-semibold mt-2"
                    style={{ color: '#fff' }}
                  >
                    {label}
                  </div>
                </div>
              ))}
            </div>
          )}

          {organizerError && (
            <div className="alert alert-danger text-start" role="alert">
              {organizerError}
            </div>
          )}

          <div className="d-flex gap-2 justify-content-center flex-wrap">
            <button
              type="button"
              onClick={() => navigate('/home')}
              className="v3-btn-primary"
            >
              <ArrowLeft size={16} />
              Back to dashboard
            </button>
            <button
              type="button"
              onClick={contactAdministrator}
              disabled={isOrganizerLoading || !!organizerError || !organizer}
              className="v3-btn-secondary"
            >
              <Mail size={16} />
              Contact administrator
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FreeEventLimitReached;
